import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Camera, CheckCircle2, AlertTriangle, ArrowLeft, KeyRound, MailCheck } from "lucide-react";

// Shared wrapper so the three pages match the Login/Register chrome.
function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6"
      style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Camera size={26} className="text-amber-400" />
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">Camera Club</div>
            <div className="text-lg font-semibold">Score Console</div>
          </div>
        </div>
        <div className="ccs-card">
          <div className="ccs-label mb-2">{subtitle}</div>
          <h1 className="text-3xl font-semibold tracking-tight mb-5">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | success | already | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMsg("This link is missing its verification token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify`, { params: { token } });
        setState(data.already_verified ? "already" : "success");
      } catch (ex) {
        setState("error");
        setMsg(formatApiError(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [token]);

  return (
    <AuthShell subtitle="Email verification" title={
      state === "loading" ? "Checking your link…" :
      state === "success" ? "You're verified ✓" :
      state === "already" ? "Already verified ✓" :
      "Couldn't verify"
    }>
      {state === "loading" && (
        <div className="muted-text text-sm">Hold tight…</div>
      )}
      {(state === "success" || state === "already") && (
        <>
          <div className="flex items-start gap-3 mb-5">
            <CheckCircle2 size={22} className="text-emerald-400 mt-0.5" />
            <p className="text-sm leading-relaxed muted-text">
              {state === "success"
                ? "Your email is confirmed. You can now use every feature of Camera Club Score."
                : "Your email is already confirmed — you don't need to do anything else."}
            </p>
          </div>
          <button
            onClick={() => navigate("/login")}
            className="ccs-btn w-full"
            data-testid="verify-continue"
          >
            Continue to sign in
          </button>
        </>
      )}
      {state === "error" && (
        <>
          <div className="flex items-start gap-3 mb-5">
            <AlertTriangle size={22} className="text-amber-400 mt-0.5" />
            <p className="text-sm leading-relaxed muted-text">
              {msg || "This verification link is invalid or has expired."}
            </p>
          </div>
          <Link to="/login" className="ccs-btn-ghost w-full flex items-center justify-center gap-2">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}


export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Endpoint always returns 200 to prevent account enumeration.
      await api.post(`/auth/forgot-password`, { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell subtitle="Account recovery" title="Forgot your password?">
      {sent ? (
        <div data-testid="forgot-password-sent">
          <div className="flex items-start gap-3 mb-5">
            <CheckCircle2 size={22} className="text-emerald-400 mt-0.5" />
            <p className="text-sm leading-relaxed muted-text">
              If an account exists for <span className="mono">{email}</span>, a
              password-reset link is on its way. The link is valid for 1 hour.
              <br /><br />
              Can&rsquo;t see the email? Check spam, or wait a minute and try again.
            </p>
          </div>
          <Link to="/login" className="ccs-btn-ghost w-full flex items-center justify-center gap-2">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="text-sm muted-text mb-4">
            Enter the email you signed up with and we&rsquo;ll send you a link
            to set a new password.
          </p>
          <div className="form-row">
            <label className="ccs-label">Email</label>
            <input
              required type="email" autoFocus className="ccs-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !email}
            className="ccs-btn w-full mt-2"
            data-testid="forgot-submit"
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <Link to="/login" className="muted-text text-xs mt-4 flex items-center justify-center gap-1">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </form>
      )}
    </AuthShell>
  );
}


export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/auth/reset-password`, { token, password });
      setDone(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell subtitle="Account recovery" title="Link missing">
        <p className="text-sm muted-text mb-5">
          The reset link is incomplete. Please request a new one.
        </p>
        <Link to="/forgot-password" className="ccs-btn w-full text-center block">
          Request a new link
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell subtitle="Account recovery" title="Password updated">
        <div className="flex items-start gap-3 mb-5">
          <CheckCircle2 size={22} className="text-emerald-400 mt-0.5" />
          <p className="text-sm muted-text">
            Your new password is set. You can now sign in with it.
          </p>
        </div>
        <button
          onClick={() => navigate("/login")}
          className="ccs-btn w-full"
          data-testid="reset-continue"
        >
          Continue to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle="Account recovery" title="Choose a new password">
      <form onSubmit={submit}>
        <div className="form-row">
          <label className="ccs-label">New password</label>
          <input
            required type="password" minLength="6" autoFocus className="ccs-input mono"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-new"
          />
        </div>
        <div className="form-row">
          <label className="ccs-label">Confirm new password</label>
          <input
            required type="password" minLength="6" className="ccs-input mono"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
          />
        </div>
        {err && <div className="danger-text text-sm mb-2">{err}</div>}
        <button
          type="submit"
          disabled={busy || !password || !confirm}
          className="ccs-btn w-full mt-2 flex items-center justify-center gap-2"
          data-testid="reset-submit"
        >
          <KeyRound size={14} /> {busy ? "Saving…" : "Set new password"}
        </button>
      </form>
    </AuthShell>
  );
}


const ROLE_BLURB = {
  ADMIN: "full access to manage members, seasons and all data",
  SECRETARY: "score competitions and manage photographers",
  VIEWER: "read-only access to results & statistics",
  MEMBER: "see your own entries, scores and history",
};

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  // info phase | form phase | error phase
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErr("This invitation link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/invite/info", { params: { token } });
        setInfo(data);
        setName(data.name || "");
        setState("form");
      } catch (ex) {
        setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        setState("error");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const user = await acceptInvite({
        token, password, name: name.trim() || undefined,
        terms_accepted: termsAccepted,
      });
      // Option A: drop them straight into the app on the right landing page.
      navigate(user.role === "MEMBER" ? "/me" : "/", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <AuthShell subtitle="Club invitation" title="Checking your invitation…">
        <div className="muted-text text-sm">Hold tight…</div>
      </AuthShell>
    );
  }
  if (state === "error") {
    return (
      <AuthShell subtitle="Club invitation" title="Invitation can't be used">
        <div className="flex items-start gap-3 mb-5" data-testid="invite-error">
          <AlertTriangle size={22} className="text-amber-400 mt-0.5" />
          <p className="text-sm leading-relaxed muted-text">
            {err || "This invitation link is invalid, has expired, or has already been accepted."}
            <br /><br />
            Ask your club administrator to send you a new invitation.
          </p>
        </div>
        <Link to="/login" className="ccs-btn-ghost w-full flex items-center justify-center gap-2">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      subtitle="Club invitation"
      title={`Welcome to ${info?.club_name || "the club"}`}
    >
      <div className="flex items-start gap-3 mb-5">
        <MailCheck size={22} className="text-emerald-400 mt-0.5 shrink-0" />
        <p className="text-sm leading-relaxed muted-text">
          You&rsquo;re joining as{" "}
          <span className="tag" data-testid="invite-role-tag">{info?.role}</span>
          {" — "}
          {ROLE_BLURB[info?.role] || "a club member"}.
          <br />
          Signing in as{" "}
          <span className="mono" data-testid="invite-email">{info?.email}</span>.
        </p>
      </div>

      <form onSubmit={submit}>
        <div className="form-row">
          <label className="ccs-label">Your name</label>
          <input
            className="ccs-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="How should we display your name?"
            data-testid="invite-name-input"
          />
        </div>

        <div className="form-row">
          <label className="ccs-label">Choose a password *</label>
          <input
            required type="password" minLength="6" autoFocus className="ccs-input mono"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 6 characters"
            data-testid="invite-password-input"
          />
        </div>

        <div className="form-row">
          <label className="ccs-label">Confirm password *</label>
          <input
            required type="password" minLength="6" className="ccs-input mono"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="invite-confirm-input"
          />
        </div>

        {err && <div className="danger-text text-sm mb-2" data-testid="invite-form-error">{err}</div>}

        <label className="flex items-start gap-2 text-sm mb-3 cursor-pointer" data-testid="invite-terms-row">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 accent-amber-400"
            data-testid="invite-terms-checkbox"
          />
          <span className="muted-text">
            I have read and agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener" className="text-amber-400 hover:text-amber-300" data-testid="invite-terms-link">
              Terms &amp; Conditions
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={busy || !password || !confirm || !termsAccepted}
          className="ccs-btn w-full mt-2 flex items-center justify-center gap-2"
          data-testid="invite-submit"
        >
          <KeyRound size={14} /> {busy ? "Setting up…" : "Accept & sign in"}
        </button>
      </form>
    </AuthShell>
  );
}

